import React, { useEffect, useState } from "react";
import { CheckCircle, Circle, X } from "@phosphor-icons/react";
import { api } from "@/lib/api";

// First-run onboarding checklist — slim 3-step guided flow docked bottom-left.
// Persistence: users can dismiss (localStorage `wp_onboarding_dismissed`) and
// completed steps are also cached so the checklist stays hidden once done.
// Highest-value growth investment: reduces week-1 churn by giving new users a
// clear "here's your next action" path instead of a cold blank dashboard.
const DISMISS_KEY = "wp_onboarding_dismissed";
const PROGRESS_KEY = "wp_onboarding_progress_v1";

export default function OnboardingChecklist({ onGoTab }) {
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(DISMISS_KEY) === "1"; } catch { return false; }
  });
  const [state, setState] = useState({ hasSlate: false, hasLineups: false });
  const [loading, setLoading] = useState(true);

  const check = async () => {
    try {
      const [slate, lineups] = await Promise.allSettled([
        api.slateInfo(),
        api.getLineups?.() ?? Promise.resolve(null),
      ]);
      const hasSlate = slate.status === "fulfilled" && !!(slate.value?.n_games > 0 || slate.value?.player_count > 0);
      const hasLineups = lineups.status === "fulfilled" && !!(lineups.value?.count > 0);
      const next = { hasSlate, hasLineups };
      setState(next);
      try { localStorage.setItem(PROGRESS_KEY, JSON.stringify(next)); } catch { /* noop */ }
    } catch (_) { /* silent */ }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (dismissed) { setLoading(false); return; }
    check();
    const onRefresh = () => check();
    window.addEventListener("wp:pool-refreshed", onRefresh);
    window.addEventListener("wp:lineups-generated", onRefresh);
    return () => {
      window.removeEventListener("wp:pool-refreshed", onRefresh);
      window.removeEventListener("wp:lineups-generated", onRefresh);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dismissed]);

  const handleDismiss = () => {
    setDismissed(true);
    try { localStorage.setItem(DISMISS_KEY, "1"); } catch { /* noop */ }
  };

  if (dismissed || loading) return null;
  // Auto-hide once all three steps are done — the checklist has served its
  // purpose and shouldn't linger on an already-onboarded workspace.
  if (state.hasSlate && state.hasLineups) return null;

  const steps = [
    {
      done: state.hasSlate,
      label: "Load your DraftKings slate",
      hint: "Slate Selection tab → pick a slate chip → Apply",
      tab: "uploads",
    },
    {
      done: state.hasSlate,
      label: "Configure your contest posture",
      hint: "Slate Intel → pick Milly Maker / Custom / etc.",
      tab: "data",
    },
    {
      done: state.hasLineups,
      label: "Build your first lineup batch",
      hint: "Optimizer → Generate Lineups",
      tab: "optimizer",
    },
  ];
  const doneCount = steps.filter((s) => s.done).length;

  return (
    <div
      data-testid="onboarding-checklist"
      className="fixed bottom-6 left-6 z-40 w-[320px] bg-[#0a0a0a] border border-[#39FF14]/40 shadow-[0_0_32px_-12px_#39FF14]"
    >
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-zinc-900 bg-black">
        <div className="flex items-center gap-2">
          <span className="mono text-[10px] uppercase tracking-widest text-[#39FF14] font-bold">
            Getting Started · {doneCount}/{steps.length}
          </span>
        </div>
        <button
          type="button"
          onClick={handleDismiss}
          data-testid="onboarding-dismiss"
          title="Dismiss (won't show again)"
          className="text-zinc-500 hover:text-white transition-colors"
        >
          <X size={12} />
        </button>
      </div>
      <div className="p-2 space-y-1">
        {steps.map((s, i) => (
          <button
            key={s.tab || s.label}
            type="button"
            onClick={() => onGoTab?.(s.tab)}
            data-testid={`onboarding-step-${i + 1}`}
            disabled={s.done}
            className={`w-full flex items-start gap-2 px-2 py-2 text-left transition-colors ${
              s.done ? "opacity-60" : "hover:bg-zinc-950"
            }`}
          >
            {s.done ? (
              <CheckCircle size={14} weight="fill" className="text-[#39FF14] mt-0.5 shrink-0" />
            ) : (
              <Circle size={14} className="text-zinc-500 mt-0.5 shrink-0" />
            )}
            <div className="flex-1 min-w-0">
              <div className={`text-[11px] font-semibold ${s.done ? "text-zinc-500 line-through" : "text-white"}`}>
                {s.label}
              </div>
              <div className="mono text-[9px] uppercase tracking-widest text-zinc-500 mt-0.5">
                {s.hint}
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
